<template>
  <div class="autocomplete">
    <div
      :class="[{'autocomplete__searching' : showResults}]"
      class="autocomplete__box"
    >
      <div class="autocomplete__input">
        <input
          ref="input"
          v-model="display"
          :placeholder="placeholder"
          :disabled="disableInput"
          :class="inputClass"
          type="text"
          @click="search"
          @input="search"
          @keydown.enter.prevent="enter"
          @keydown.tab="close"
          @keydown.up="up"
          @keydown.down="down"
          @keydown.esc.stop="close"
          @focus="focus"
          @blur="blur"
        >
        <input
          :name="name"
          :value="value"
          type="hidden"
        >
        <!-- <span
          v-show="!disableInput && !isEmpty && !isLoading && !hasError"
          class="autocomplete__icon autocomplete--clear"
          @click="clear"
        >
          <icon
            v-if="clearButtonIcon"
            icon="tag-remove"
            style="cursor: pointer;"
            class="text-gray-600"
          />
        </span> -->
        <div class="autocomplete__icon">
          <icon
            v-if="showResults"
            icon="chevron-up"
          />
          <icon
            v-else
            icon="chevron-down"
          />
        </div>
      </div>
    </div>

    <ul
      v-show="showResults"
      :style="listStyle"
      class="autocomplete__results"
    >
      <slot name="results">
        <!-- error -->
        <li
          v-if="hasError"
          class="autocomplete__results__item autocomplete__results__item--error"
        >
          {{ error }}
        </li>

        <!-- results -->
        <template v-if="!hasError">
          <slot name="firstResult" />
          <li
            v-for="(result, key) in results"
            :key="key"
            :class="{'autocomplete__selected' : isSelected(key) }"
            class="autocomplete__results__item"
            @click.prevent="select(result)"
          >
            <slot
              :result="result"
              name="result"
            >
              <span
                v-html="formatDisplay(result)"
              />
            </slot>
          </li>
          <slot name="lastResult" />
        </template>

        <!-- no results -->
        <li
          v-if="noResultMessage"
          class="autocomplete__results__item autocomplete__no-results"
        >
          <slot name="noResults">
            No Results.
          </slot>
        </li>
      </slot>
    </ul>
  </div>
</template>

<script type="text/babel">
import {debounce} from '@/helpers'

export default {
  props: {
    /**
     * Data source for the results
     *   `String` is a url, typed input will be appended
     *   `Function` received typed input, and must return a string; to be used as a url
     *   `Array` and `Object` (see `results-property`) are used directly
     */
    source: {
      type: [String, Function, Array, Object],
      required: true,
    },
    allowCreation: {
      required: false,
      default: false,
      type: Boolean,
    },
    /**
     * Input placeholder
     */
    placeholder: {
      type: String,
      default: 'Search',
    },
    /**
     * Preset starting value
     */
    initialValue: {
      type: [String, Number],
      default: '',
    },
    /**
     * Preset starting display value
     */
    initialDisplay: {
      type: String,
      default: '',
    },
    /**
     * CSS class for the surrounding input div
     */
    inputClass: {
      type: [String, Object, Array],
      default: 'form-control',
    },
    /**
     * To disable the input
     */
    disableInput: {
      type: Boolean,
      default: false,
    },
    /**
     * name property of the input holding the selected value
     */
    name: {
      type: String,
      default: 'autocomplete',
    },
    /**
     * api - property of results array
     */
    resultsProperty: {
      type: String,
      default: null,
    },
    /**
     * Results property used as the value
     */
    resultsValue: {
      type: String,
      default: 'id',
    },
    /**
     * Results property used as the display
     */
    resultsDisplay: {
      type: [String, Function],
      default: 'name',
    },
    /**
     * Whether to show the no results message
     */
    showNoResults: {
      type: Boolean,
      default: true,
    },

    minSearchChars: {
      default: 0,
      required: false,
      type: Number,
    },
    /**
     * Additional request headers
     */
    requestHeaders: {
      type: Object,
      default () {
        return {}
      },
    },
    /**
     * Optional clear button icon class
     */
    clearButtonIcon: {
      type: String,
      default: 'sl-close',
    },

  },
  data () {
    return {
      value: null,
      display: null,
      results: null,
      selectedIndex: null,
      loading: false,
      isFocussed: false,
      error: null,
      selectedId: null,
      selectedDisplay: null,
      eventListener: false,
    }
  },
  computed: {
    showResults () {
      return Array.isArray(this.results) || this.hasError
    },
    noResults () {
      return Array.isArray(this.results) && this.results.length === 0
    },
    noResultMessage () {
      return this.noResults &&
        !this.isLoading &&
        this.isFocussed &&
        !this.hasError &&
        this.showNoResults
    },
    isEmpty () {
      return !this.display
    },
    isLoading () {
      return this.loading === true
    },
    hasError () {
      return this.error !== null
    },
    listStyle () {
      if (this.isLoading) {
        return {
          color: '#ccc',
        }
      }
      return ''
    },
  },

  watch: {
    initialValue () {
      this.value = this.initialValue
    },

    initialDisplay () {
      this.selectedDisplay = this.initialDisplay
      this.display = this.selectedDisplay
    },
  },

  mounted () {
    this.value = this.initialValue
    this.display = this.initialDisplay
    if (!this.display && this.value && typeof this.source === 'object') {
      for (const val of this.source) {
        if (val[this.resultsValue] === this.value) {
          this.display = this.formatDisplay(val)
          continue
        }
      }
    }
    this.selectedDisplay = this.display
    this.$on('focus', () => this.$refs.input.focus())
  },

  methods: {
    /**
     * Search wrapper method
     */
    search () {
      this.selectedIndex = null
      switch (true) {
        case typeof this.source === 'string':
          // No resource search with no input
          if (!this.display || this.display.length < 1) {
            return
          }
          return this.resourceSearch(this.source + this.display)
        case typeof this.source === 'function':
          // No resource search with no input
          if (!this.display || this.display.length < 1) {
            return
          }
          return this.resourceSearch(this.source(this.display))
        case Array.isArray(this.source):
          return this.arrayLikeSearch()
        default:
          throw new TypeError()
      }
    },
    /**
     * Debounce the typed search query before making http requests
     * @param {String} url
     */
    resourceSearch: debounce(function (url) {
      if (!this.display || this.display.length <= this.minSearchChars) {
        this.results = []
        return
      }
      this.loading = true
      this.setEventListener()
      this.request(url)
    }, 200),
    /**
     * Make an http request for results
     * @param {String} url
     */
    request (url) {
      const httpClient = this.$http
      return httpClient.get(url)
          .then((response) => {
            this.results = this.setResults(response.data)
            if (this.allowCreation) {
              const creationOption = {
                id: 0,
                _create: true,
              }
              creationOption[this.resultsDisplay] = 'Create ' + this.display
              this.results.push(creationOption)
            }
            if (this.results.length === 0) {
              this.$emit('noResults', {query: this.display})
            } else {
              this.$emit('results', {results: this.results})
              this.$nextTick(() => this.down())
            }
            this.loading = false
          })
          .catch((error) => {
            this.error = error.message
            this.loading = false
          })
    },
    /**
     * Set some default headers and apply user supplied headers
     */
    getHeaders () {
      const headers = {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      }
      if (this.requestHeaders) {
        for (const prop in this.requestHeaders) {
          if (this.requestHeaders[prop]) {
            headers[prop] = this.requestHeaders[prop]
          }
        }
      }
      return new Headers(headers)
    },
    /**
     * Set results property from api response
     * @param {Object|Array} response
     * @return {Array}
     */
    setResults (response) {
      if (this.resultsProperty && response[this.resultsProperty]) {
        return response[this.resultsProperty]
      }
      if (Array.isArray(response)) {
        return response
      }
      return []
    },
    /**
     * Search in results passed via an array
     */
    arrayLikeSearch () {
      this.setEventListener()
      if (!this.display) {
        this.results = this.source
        this.$emit('results', {results: this.results})
        this.loading = false
        return true
      }
      this.results = this.source.filter((item) => {
        return this.formatDisplay(item).toLowerCase().includes(this.display.toLowerCase())
      })
      this.$emit('results', {results: this.results})
      this.loading = false
    },
    /**
     * Select a result
     * @param {Object}
     */
    select (obj) {
      if (!obj) {
        return
      }
      if (obj._create) {
        this.$emit('create', this.formatDisplay(obj).substring(7))
      } else {
        this.value = (this.resultsValue && obj[this.resultsValue]) ? obj[this.resultsValue] : obj.id
        this.display = this.formatDisplay(obj)
        this.selectedDisplay = this.display
        this.$emit('selected', {
          value: this.value,
          display: this.display,
          object: obj,
        })
        this.$emit('input', this.value)
      }
      this.close()
    },
    /**
     * @param  {Object} obj
     * @return {String}
     */
    formatDisplay (obj) {
      switch (typeof this.resultsDisplay) {
        case 'function':
          return this.resultsDisplay(obj)
        case 'string':
          if (!obj[this.resultsDisplay]) {
            throw new Error(`"${this.resultsDisplay}" property expected on result but is not defined.`)
          }
          return obj[this.resultsDisplay]
        default:
          throw new TypeError()
      }
    },
    /**
     * Register the component as focussed
     */
    focus () {
      this.isFocussed = true
    },
    /**
     * Remove the focussed value
     */
    blur () {
      this.isFocussed = false
    },
    /**
     * Is this item selected?
     * @param {Object}
     * @return {Boolean}
     */
    isSelected (key) {
      return key === this.selectedIndex
    },
    /**
     * Focus on the previous results item
     */
    up () {
      if (this.selectedIndex === null) {
        this.selectedIndex = this.results.length - 1
        return
      }
      this.selectedIndex = (this.selectedIndex === 0) ? this.results.length - 1 : this.selectedIndex - 1
    },
    /**
     * Focus on the next results item
     */
    down () {
      if (this.selectedIndex === null) {
        this.selectedIndex = 0
        return
      }
      this.selectedIndex = (this.selectedIndex === this.results.length - 1) ? 0 : this.selectedIndex + 1
    },
    /**
     * Select an item via the keyboard
     */
    enter () {
      if (this.selectedIndex === null) {
        this.$emit('nothingSelected', this.display)
        return
      }
      this.select(this.results[this.selectedIndex])
      this.$emit('enter', this.display)
    },
    /**
     * Clear all values, results and errors
     */
    clear () {
      this.display = null
      this.value = null
      this.results = null
      this.error = null
      this.$emit('clear')
    },
    /**
     * Close the results list. If nothing was selected clear the search
     */
    close () {
      if (!this.value || !this.selectedDisplay) {
        this.clear()
      }
      if (this.selectedDisplay !== this.display && this.value) {
        this.display = this.selectedDisplay
      }
      this.results = null
      this.error = null
      this.removeEventListener()
      this.$emit('close')
    },
    /**
     * Add event listener for clicks outside the results
     */
    setEventListener () {
      if (this.eventListener) {
        return false
      }
      this.eventListener = true
      document.addEventListener('click', this.clickOutsideListener, true)
      return true
    },
    /**
     * Remove the click event listener
     */
    removeEventListener () {
      this.eventListener = false
      document.removeEventListener('click', this.clickOutsideListener, true)
    },
    /**
     * Method invoked by the event listener
     */
    clickOutsideListener (event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.close()
      }
    },
  },
}
</script>

<style lang="scss">
  .autocomplete {
    position: relative;
    width: 100%;

    &__input {
      position: relative;
      input {
        position: relative;
        width: 100%;
        // padding: 0.5rem;
        border-color: $border-gray;
      }
    }

    &--clear {
      &:hover {
        color: $red;
      }
      cursor: pointer;
      transition: color 0.5s;
      position: absolute;
      color: darken($red, 25%);
      right: $spacer * 0.75;
    }

    &__results {
      position: absolute;
      margin: 0;
      padding: 0;
      list-style-type: none;
      z-index: 1000;
      max-height: 400px;
      overflow-y: auto;
      background: #fff;
      width: 100%;
      border: 1px solid $border-gray;
      border-top: 0;
      color: #000;

      &__item--error {
        color: #f00;
      }

      &__item {
        height: 3rem;
        padding: 0.5rem;
        display: flex;
        align-items: center;
        cursor: pointer;

        &:hover {
          background: rgba(0, 180, 255, 0.075);
        }

        &.autocomplete__selected {
          background: rgba(0, 180, 255, 0.15);
        }
      }
    }

    &__icon {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      .i {
        color: $gray-600;
      }
    }
  }

  .animate-spin {
    animation: spin 2s infinite linear;
  }

  @-moz-keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @-webkit-keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @-o-keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  /* Custom autocomplete design */

  .navbar__input,
  .fr-inline .fr-element {
    border: 1px solid $input-border-color;
  }

  .form-control:focus,
  .fr-inline .fr-element:focus,
  .navbar__input:focus {
    box-shadow: none !important;
  }

  .autocomplete__searching .autocomplete__input .form-control {
    box-shadow: none;
    border-color: $input-border-color;
    border-radius: 4px 4px 0px 0px;
    box-shadow: none !important;
  }

  .autocomplete__results {
    border-radius: 0px 0px 4px 4px;
    border-color: $input-border-color $input-border-color $input-border-color $input-border-color !important;
    border-top-width: 2px;
  }

  .autocomplete__results__item {
    background-color: #fff;
  }

  .autocomplete__results__item.autocomplete__selected {
    font-weight: bold;
    background: $tellimer-hover !important;
    color: $tellimer;
  }

  .autocomplete__results__item:hover {
    background: $tellimer-hover !important;
    color: $tellimer;
  }

</style>
